import React, { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Stack,
  Button,
  Divider
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import Badge from './Badge';

interface VoiceOfCustomerComment {
  id: number;
  orderNumber: string;
  routeId: string;
  deliveryComment: string;
  careComment: string;
  friendliness: string;
  presentation: string;
  correctiveFormUrl?: string;
  deliveryDate: string;
  deliveryWindow: string;
  createdOnUtc: string;
  updatedOnUtc: string;
  shiftId: number;
}

interface VoiceOfCustomerCommentsProps {
  comments: VoiceOfCustomerComment[];
}

const VoiceOfCustomerComments: React.FC<VoiceOfCustomerCommentsProps> = ({
  comments
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [sentimentFilter, setSentimentFilter] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setRatingFilter('all');
    setSentimentFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || ratingFilter !== 'all' || sentimentFilter !== 'all';

  // Calculate averages
  const calculateAverage = (field: 'friendliness' | 'presentation') => {
    if (!comments.length) return 0;
    const sum = comments.reduce(
      (acc, comment) => acc + parseInt(comment[field], 10),
      0
    );
    return (sum / comments.length).toFixed(1);
  };

  const avgFriendliness = parseFloat(calculateAverage('friendliness') || '');
  const avgPresentation = parseFloat(calculateAverage('presentation') || '');

  // Check if comment indicates issues
  const hasIssues = (comment: VoiceOfCustomerComment) => {
    const issueKeywords = [
      'shortage',
      'complaint',
      'problem',
      'issue',
      'not happy',
      'disappointed',
      'poor',
      'bad'
    ];
    const text = `${comment.deliveryComment} ${comment.careComment}`.toLowerCase();
    return issueKeywords.some((keyword) => text.includes(keyword));
  };

  // Get sentiment for a comment
  const getSentiment = (comment: VoiceOfCustomerComment) => {
    if (hasIssues(comment)) return 'negative';
    const positiveKeywords = [
      'good',
      'great',
      'excellent',
      'satisfied',
      'happy',
      'pleased',
      'perfect'
    ];
    const text = `${comment.deliveryComment} ${comment.careComment}`.toLowerCase();
    if (positiveKeywords.some((keyword) => text.includes(keyword))) {
      return 'positive';
    }
    return 'neutral';
  };

  // Get average rating for a comment
  const getAverageRating = (comment: VoiceOfCustomerComment) =>
    (parseInt(comment.friendliness, 10) + parseInt(comment.presentation, 10)) / 2;

  // Filter comments based on search and filters
  const filteredComments = useMemo(
    () =>
      comments.filter((comment) => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm
          || comment.orderNumber.toLowerCase().includes(searchLower)
          || comment.deliveryComment.toLowerCase().includes(searchLower)
          || comment.careComment.toLowerCase().includes(searchLower)
          || comment.routeId.toLowerCase().includes(searchLower);

        // Rating filter
        const avgRating = getAverageRating(comment);
        const matchesRating = ratingFilter === 'all'
          || (ratingFilter === 'high' && avgRating >= 7)
          || (ratingFilter === 'medium' && avgRating >= 5 && avgRating < 7)
          || (ratingFilter === 'low' && avgRating < 5);

        // Sentiment filter
        const sentiment = getSentiment(comment);
        const matchesSentiment = sentimentFilter === 'all' || sentiment === sentimentFilter;
        return matchesSearch && matchesRating && matchesSentiment;
      }),
    [comments, searchTerm, ratingFilter, sentimentFilter]
  );

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const openCorrectiveForm = (url: string) => {
    window.open(url, '_blank');
  };

  const getStatusChip = (comment: VoiceOfCustomerComment) => {
    const hasIssue = hasIssues(comment);
    const sentiment = getSentiment(comment);
    const avgRating = getAverageRating(comment);

    if (hasIssue) {
      return (
        <Chip
          label="Needs Attention"
          color="warning"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      );
    }

    if (sentiment === 'positive' || avgRating >= 8) {
      return (
        <Chip
          label="Excellent"
          color="success"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      );
    }

    if (sentiment === 'negative' || avgRating < 5) {
      return (
        <Chip
          label="Poor"
          color="error"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      );
    }

    return (
      <Chip
        label="Average"
        color="info"
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  if (!comments || comments.length === 0) {
    return (
      <Typography variant="body1" margin="10px" paddingTop="10px">
        No voice of customer comments available.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Filters Section */}
      <Card sx={{ marginBottom: 3, boxShadow: 'none', border: 'none' }}>
        <CardHeader
          title={(
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Search & Filter Comments
              </Typography>
              {hasActiveFilters && (
                <Chip
                  label={`${filteredComments.length} of ${comments.length}`}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          )}
          action={(
            hasActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Clear Filters
              </Button>
            )
          )}
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by order, comments, route..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Rating Filter</InputLabel>
                <Select
                  value={ratingFilter}
                  label="Rating Filter"
                  onChange={(e) => setRatingFilter(e.target.value)}
                >
                  <MenuItem value="all">All Ratings</MenuItem>
                  <MenuItem value="high">High (7-10)</MenuItem>
                  <MenuItem value="medium">Medium (5-6)</MenuItem>
                  <MenuItem value="low">Low (1-4)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sentiment</InputLabel>
                <Select
                  value={sentimentFilter}
                  label="Sentiment"
                  onChange={(e) => setSentimentFilter(e.target.value)}
                >
                  <MenuItem value="all">All Sentiments</MenuItem>
                  <MenuItem value="positive">Positive</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                  <MenuItem value="negative">Negative</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  textAlign="center"
                >
                  Overall Averages
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Badge value={avgFriendliness} label="F" />
                  <Badge value={avgPresentation} label="P" />
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" color="textSecondary">
                  Active filters:
                </Typography>
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    size="small"
                    onDelete={() => setSearchTerm('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {ratingFilter !== 'all' && (
                  <Chip
                    label={`Rating: ${ratingFilter}`}
                    size="small"
                    onDelete={() => setRatingFilter('all')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {sentimentFilter !== 'all' && (
                  <Chip
                    label={`Sentiment: ${sentimentFilter}`}
                    size="small"
                    onDelete={() => setSentimentFilter('all')}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Individual Comments */}
      {filteredComments.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No comments found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {hasActiveFilters
                ? 'Try adjusting your search criteria or clearing filters.'
                : 'No voice of customer comments are available at this time.'}
            </Typography>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                sx={{ mt: 2, textTransform: 'none' }}
                variant="outlined"
              >
                Clear All Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Box>
          {/* Results summary */}
          <Box
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="body2" color="textSecondary">
              Showing {filteredComments.length} of {comments.length} comments
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Click on any comment card to view details
            </Typography>
          </Box>

          {filteredComments.map((comment) => {
            const hasIssue = hasIssues(comment);
            const isExpanded = expandedItems.has(comment.id);

            return (
              <Card
                key={comment.id}
                sx={{
                  marginBottom: 2,
                  transition: 'all 0.3s ease-in-out',
                  border: '1px solid',
                  borderColor: hasIssue ? 'warning.main' : 'divider',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    borderColor: hasIssue ? 'warning.dark' : 'primary.main'
                  },
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Expandable indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: isExpanded
                      ? 'primary.main'
                      : 'transparent',
                    transition: 'background-color 0.3s ease-in-out'
                  }}
                />

                <CardHeader
                  avatar={
                    hasIssue ? (
                      <WarningIcon color="warning" sx={{ fontSize: 28 }} />
                    ) : undefined
                  }
                  title={(
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={{ xs: 1, sm: 2 }}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        Order #{comment.orderNumber}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {comment.deliveryWindow}
                      </Typography>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{
                          flexWrap: 'wrap',
                          gap: { xs: 0.5, sm: 1 }
                        }}
                      >
                        <Badge
                          value={parseInt(comment.friendliness, 10)}
                          label="Friendliness"
                        />
                        <Badge
                          value={parseInt(comment.presentation, 10)}
                          label="Presentation"
                        />
                        {getStatusChip(comment)}
                      </Stack>
                    </Box>
                  )}
                  action={(
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {comment.correctiveFormUrl && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            openCorrectiveForm(comment.correctiveFormUrl || '');
                          }}
                          size="small"
                          title="Open Corrective Action Form"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              color: 'primary.main'
                            }
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(comment.id);
                        }}
                        size="small"
                        title={
                          isExpanded ? 'Collapse details' : 'Expand details'
                        }
                        sx={{
                          transform: isExpanded
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            color: 'primary.main'
                          }
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                  )}
                  sx={{
                    cursor: 'pointer',
                    py: { xs: 1, sm: 2 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    },
                    '& .MuiCardHeader-content': {
                      overflow: 'hidden'
                    }
                  }}
                  onClick={() => toggleExpand(comment.id)}
                />

                <Collapse in={isExpanded} timeout={300}>
                  <CardContent
                    sx={{ backgroundColor: 'grey.50' }}
                  >
                    <Grid container spacing={3}>
                      {/* Order Details */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 1 }}
                          >
                            Order Details
                          </Typography>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            divider={(
                              <Box
                                sx={{
                                  borderLeft: '1px solid',
                                  borderColor: 'divider',
                                  height: 20
                                }}
                              />
                            )}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Route ID
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {comment.routeId}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Delivery Date
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {comment.deliveryDate}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Shift ID
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {comment.shiftId}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>

                      {/* Comments Section */}
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                padding: 2,
                                backgroundColor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                height: '100%'
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                Delivery Feedback
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontStyle: comment.deliveryComment
                                    ? 'normal'
                                    : 'italic',
                                  color: comment.deliveryComment
                                    ? 'text.primary'
                                    : 'text.secondary',
                                  lineHeight: 1.6
                                }}
                              >
                                {comment.deliveryComment || 'No feedback provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                padding: 2,
                                backgroundColor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                height: '100%'
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                Care & Service Feedback
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontStyle: comment.careComment
                                    ? 'normal'
                                    : 'italic',
                                  color: comment.careComment
                                    ? 'text.primary'
                                    : 'text.secondary',
                                  lineHeight: 1.6
                                }}
                              >
                                {comment.careComment || 'No feedback provided'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Ratings Section */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 2 }}
                          >
                            Customer Ratings
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={3}
                            alignItems="center"
                          >
                            <Badge
                              value={parseInt(comment.friendliness, 10)}
                              label="Friendliness"
                            />
                            <Badge
                              value={parseInt(comment.presentation, 10)}
                              label="Presentation"
                            />
                            <Box sx={{ ml: 'auto' }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Average Rating
                              </Typography>
                              <Typography
                                variant="h6"
                                color="primary"
                                sx={{ fontWeight: 600 }}
                              >
                                {getAverageRating(comment).toFixed(1)}/10
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>

                      {/* Issue Alert */}
                      {hasIssue && (
                        <Grid item xs={12}>
                          <Alert
                            severity="warning"
                            sx={{
                              backgroundColor: 'warning.light',
                              '& .MuiAlert-icon': {
                                color: 'warning.main'
                              }
                            }}
                            icon={<WarningIcon />}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Attention Required: This feedback contains
                              potential issues that may need follow-up action.
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default VoiceOfCustomerComments;
